import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';

// ... existing code ...

const schemaData = {
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/why-dr-villarreal#webpage`,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/why-dr-villarreal`,
  name: 'Why Dr. Osmel Villarreal | ACHC Certified Consultant DBA MBA MS',
  description: 'Dr. Osmel Villarreal, DBA, MBA, MS — ACHC Certified Consultant and healthcare entrepreneur with doctoral research expertise in home health operations and compliance.',
  isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website` },
  mainEntity: {
    '@type': 'Person',
    name: 'Dr. Osmel Villarreal',
    jobTitle: 'ACHC Certified Consultant',
    description: 'Doctor of Business Administration (DBA), MBA, MS. Healthcare entrepreneur and ACHC Certified Consultant with real-world experience opening and managing home health agencies.',
    hasCredential: [
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'Doctor of Business Administration (DBA)' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'Master of Business Administration (MBA)' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'Master of Science (MS)' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'ACHC Certified Consultant' },
    ],
    worksFor: {
      '@type': 'Organization',
      name: 'Advantixx Home Health Accreditation Consultants',
      url: import.meta.env.VITE_SITE_URL || 'https://example.com',
    },
    knowsAbout: ['ACHC Accreditation', 'Home Health Compliance', 'Healthcare Operations', 'Mock Surveys', 'Policy Development'],
  },
};

export default function WhyDrVillarrealPage() {
  useSEO({
    title: 'Why Dr. Osmel Villarreal | ACHC Certified Consultant DBA MBA MS',
    description: 'Dr. Osmel Villarreal, DBA, MBA, MS — ACHC Certified Consultant and healthcare entrepreneur with doctoral research expertise in home health operations and compliance.',
    keywords: 'Dr. Osmel Villarreal, ACHC certified consultant, DBA MBA MS, home health expert, healthcare consultant',
    canonical: '/why-dr-villarreal',
  });

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-why-dr-villarreal';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-why-dr-villarreal');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="bg-[#0d0d0d] min-h-screen">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative pt-40 pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ca99214ae0e4689b2aebc3ab42cfb9ad.jpg"
            alt="Dr. Osmel Villarreal consulting office"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/95 via-[#0d0d0d]/80 to-[#0d0d0d]/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/70 via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-[#d4af37]"></div>
            <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Proven Expertise</span>
          </div>
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] mb-6">
              Why Choose
              <br />
              <span className="text-[#d4af37]">Dr. Osmel Villarreal</span>
              <br />
              as Your Consultant
            </h1>
            <p className="text-lg md:text-xl text-[#b8b8b8] leading-relaxed mb-8 max-w-2xl">
              DBA, MBA, MS — Doctoral Researcher, Healthcare Entrepreneur, and ACHC Accreditation Expert. You are not just hiring a consultant — you are partnering with a proven industry leader who has built and operated multiple healthcare organizations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="whitespace-nowrap bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-8 py-4 rounded-full hover:bg-[#f4c430] transition-all duration-300 text-center cursor-pointer"
              >
                Book a Consultation
              </Link>
              <a
                href="#research"
                className="whitespace-nowrap border-2 border-[#d4af37] text-[#d4af37] font-bold text-sm px-8 py-4 rounded-full hover:bg-[#d4af37] hover:text-[#0d0d0d] transition-all duration-300 text-center cursor-pointer"
              >
                View Doctoral Research
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── CREDENTIALS STRIP ── */}
      <section className="bg-[#111111] py-14 border-y border-[#d4af37]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex justify-center mb-10">
            <a
              href="https://www.achc.org/certified-consultants.html"
              target="_blank"
              rel="nofollow noreferrer"
              className="group flex items-center gap-8 bg-white/5 border border-white/10 rounded-2xl px-10 py-6 hover:bg-white/8 hover:border-white/20 transition-all duration-300 cursor-pointer"
              title="ACHC Certified Consultant — Verify on ACHC.org"
            >
              <img
                src="https://storage.readdy-site.link/project_files/08c9742b-b998-4dd6-add2-07c3775679f4/889fdc5b-3fe1-4cf9-8f22-80529d37f789_ACHC-Certified-Consultant-Badge.png?v=954a2225981db5d3506bfc978a15e293"
                alt="ACHC Certified Consultant Badge"
                className="h-20 w-auto object-contain shrink-0"
              />
              <div className="hidden sm:block w-px h-14 bg-white/10 shrink-0"></div>
              <div className="hidden sm:block">
                <p className="text-white font-black text-base mb-1">ACHC Certified Consultant</p>
                <p className="text-[#888] text-xs leading-relaxed max-w-sm">
                  ACHC is a nonprofit accreditation organization with CMS deeming authority for Home Health, Hospice, Renal Dialysis, Home Infusion Therapy, and DMEPOS — a symbol of quality and excellence since 1986.
                </p>
              </div>
              <i className="ri-external-link-line text-white/30 text-sm group-hover:text-white/60 transition-colors duration-200 shrink-0"></i>
            </a>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            {[
              { icon: 'ri-graduation-cap-line', label: 'Doctor of Business Administration (DBA)' },
              { icon: 'ri-briefcase-line', label: 'Master of Business Administration (MBA)' },
              { icon: 'ri-book-open-line', label: 'Master of Science (MS)' },
              { icon: 'ri-award-line', label: 'ACHC Certified Consultant' },
              { icon: 'ri-hospital-line', label: 'Healthcare Entrepreneur — Multiple Agencies' },
              { icon: 'ri-bar-chart-line', label: 'Evidence-Based Doctoral Researcher' },
            ].map((c) => (
              <div key={c.label} className="flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20">
                  <i className={`${c.icon} text-[#d4af37] text-xl`}></i>
                </div>
                <p className="text-[#ccc] text-xs leading-snug font-medium">{c.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROFILE + DIPLOMA ── */}
      <section className="bg-[#0d0d0d] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-[#d4af37]"></div>
                <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">About Dr. Villarreal</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-6 leading-tight">
                A Rare Combination of
                <br />
                <span className="text-[#d4af37]">Research &amp; Real-World Mastery</span>
              </h2>
              <p className="text-[#999] text-base leading-relaxed mb-5">
                Dr. Osmel Villarreal, DBA, MBA, MS brings a rare combination of doctoral-level research expertise and hands-on experience opening and managing home health agencies. His deep understanding of regulatory compliance, accreditation, and real-world operational success across multiple healthcare sectors sets him apart from every other consultant in the industry.
              </p>
              <p className="text-[#999] text-base leading-relaxed mb-8">
                When you work with Dr. Villarreal, you are not just getting advice — you are getting a complete strategy to build, grow, and scale your agency from a consultant who understands both business and healthcare regulations at the highest level.
              </p>
              <div className="flex flex-wrap gap-3">
                {['DBA', 'MBA', 'MS', 'ACHC Certified'].map((tag) => (
                  <span key={tag} className="bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-bold px-4 py-2 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-6 lg:sticky lg:top-28">
              <div className="rounded-2xl border border-[#d4af37]/30 bg-[#161616] p-3">
                <div className="mb-3 flex items-center gap-2 px-1">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-award-fill text-[#d4af37] text-sm"></i>
                  </div>
                  <span className="text-[#d4af37] text-[10px] uppercase tracking-[2px] font-bold">Doctoral Diploma — DBA · Dr. Osmel Villarreal</span>
                </div>
                <img
                  src="https://storage.readdy-site.link/project_files/08c9742b-b998-4dd6-add2-07c3775679f4/a0b178dc-fbd9-48ec-92dd-b95bde23e1e0_Doctor-Degree-Diploma---Osmel-Villarreal.jpeg?v=f26dcc5e8ddf64ad2e3615a93cc99400"
                  alt="Dr. Osmel Villarreal — Doctor of Business Administration Doctoral Diploma"
                  className="w-full h-auto rounded-xl object-contain"
                />
              </div>

              <div className="bg-[#161616] border border-[#d4af37]/20 rounded-2xl p-6 flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 border border-[#d4af37]/20 shrink-0">
                  <i className="ri-book-3-line text-[#d4af37] text-xl"></i>
                </div>
                <div>
                  <p className="text-[#d4af37] text-[10px] uppercase tracking-[2px] font-semibold mb-1">Doctoral Capstone Research</p>
                  <p className="text-white font-bold text-sm leading-snug">
                    &ldquo;Home Healthcare Providers: Enhancing Organizational Efficiency Through Effective Management Software Systems&rdquo;
                  </p>
                  <p className="text-[#777] text-xs mt-2 leading-relaxed">Qualitative research involving healthcare leaders across the United States.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DOCTORAL RESEARCH ── */}
      <section id="research" className="bg-[#111111] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#d4af37]"></div>
              <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Doctoral Research</span>
              <div className="w-8 h-px bg-[#d4af37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Doctoral-Level Expertise in<br />Home Healthcare Operations</h2>
          </div>

          <div className="bg-[#161616] border border-[#d4af37]/30 rounded-2xl p-8 md:p-10 mb-10 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#d4af37]/15 mx-auto mb-5">
              <i className="ri-book-3-line text-[#d4af37] text-2xl"></i>
            </div>
            <p className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold mb-3">Doctoral Capstone Project</p>
            <h3 className="text-white font-black text-xl md:text-2xl leading-snug max-w-2xl mx-auto mb-4">
              &ldquo;Home Healthcare Providers: Enhancing Organizational Efficiency Through Effective Management Software Systems&rdquo;
            </h3>
            <p className="text-[#888] text-sm leading-relaxed max-w-xl mx-auto">
              Extensive qualitative research involving healthcare leaders across the United States, focused on solving the biggest challenges in the home health industry today — inefficiency, compliance risks, and operational failures caused by poor systems.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { text: 'Why many home health agencies struggle with compliance and operational inefficiencies' },
              { text: 'The real barriers to successful agency growth: cost, training, workflow disruption, and poor systems' },
              { text: 'How leadership, training, and system integration directly impact agency success and sustainability' },
              { text: 'Agencies that align technology, training, and leadership achieve higher efficiency and long-term sustainability' },
            ].map((f, i) => (
              <div key={i} className="flex items-start gap-4 bg-[#161616] border border-[#d4af37]/10 rounded-xl p-6 hover:border-[#d4af37]/30 transition-all duration-300">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#d4af37]/15 shrink-0 mt-0.5">
                  <i className="ri-search-line text-[#d4af37] text-sm"></i>
                </div>
                <p className="text-[#ccc] text-sm leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT MAKES HIM DIFFERENT ── */}
      <section className="bg-[#0d0d0d] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#d4af37]"></div>
              <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">The Difference</span>
              <div className="w-8 h-px bg-[#d4af37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">What Makes This Different<br />From Other Consultants</h2>
            <p className="text-[#888] text-base max-w-xl mx-auto">Most consultants give you generic advice. Dr. Villarreal delivers evidence-based strategies backed by doctoral research and real-world execution.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-flask-line', title: 'Evidence-Based Strategies', desc: 'Every recommendation is grounded in doctoral-level research validated by healthcare leaders across the United States — not generic advice.' },
              { icon: 'ri-building-2-line', title: 'Real Agency Experience', desc: 'Dr. Villarreal has personally opened, managed, and scaled home health agencies, home care agencies, and medical practices.' },
              { icon: 'ri-shield-check-line', title: 'Compliance-First Approach', desc: 'His research proves that agencies fail without proper documentation systems and regulatory readiness. He builds compliance in from day one.' },
              { icon: 'ri-links-line', title: 'Theory Meets Practice', desc: 'Bridging academic research with real-world operations and scalable business growth — a combination virtually no other consultant offers.' },
            ].map((d) => (
              <div key={d.title} className="group bg-[#161616] border border-[#d4af37]/10 rounded-xl p-7 hover:border-[#d4af37]/40 hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 mb-5 group-hover:bg-[#d4af37]/20 transition-colors duration-300">
                  <i className={`${d.icon} text-[#d4af37] text-xl`}></i>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{d.title}</h3>
                <p className="text-[#888] text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REAL EXPERIENCE ── */}
      <section className="bg-[#111111] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-[#d4af37]"></div>
                <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Real Experience</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-5 leading-tight">
                Opening &amp; Scaling
                <br />
                <span className="text-[#d4af37]">Healthcare Businesses</span>
              </h2>
              <p className="text-[#999] text-base leading-relaxed mb-8">
                Unlike many consultants who have never operated an agency, Dr. Villarreal has successfully opened, managed, and scaled multiple healthcare organizations — giving you guidance from someone who has been exactly where you are.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: 'ri-home-heart-line', label: 'Home Health Agencies — Opened & Managed' },
                  { icon: 'ri-user-heart-line', label: 'Home Care Agencies — Built & Operated' },
                  { icon: 'ri-stethoscope-line', label: 'Medical Practices — Developed & Scaled' },
                  { icon: 'ri-file-list-3-line', label: 'Compliance, Billing & Scheduling Systems' },
                ].map((e) => (
                  <div key={e.label} className="flex items-center gap-3 bg-[#161616] border border-[#d4af37]/10 rounded-xl px-5 py-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#d4af37]/10 shrink-0">
                      <i className={`${e.icon} text-[#d4af37] text-base`}></i>
                    </div>
                    <span className="text-[#ccc] text-sm font-medium leading-snug">{e.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5">
              {[
                { icon: 'ri-search-eye-line', title: 'Faced Inspections & Audits', desc: 'Real experience navigating regulatory scrutiny and emerging compliant.' },
                { icon: 'ri-tools-line', title: 'Built Systems from Scratch', desc: 'Created operational frameworks for agencies starting from zero.' },
                { icon: 'ri-puzzle-line', title: 'Solved Real Operational Challenges', desc: 'Hands-on problem solving across billing, scheduling, and clinical operations.' },
                { icon: 'ri-map-pin-line', title: 'Navigated Licensing & Accreditation', desc: 'Guided agencies through every regulatory milestone from licensure to ACHC.' },
              ].map((r) => (
                <div key={r.title} className="bg-[#161616] border border-[#d4af37]/10 rounded-xl p-6 hover:border-[#d4af37]/30 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#d4af37]/10 mb-4">
                    <i className={`${r.icon} text-[#d4af37] text-lg`}></i>
                  </div>
                  <h3 className="text-white font-bold text-sm mb-2">{r.title}</h3>
                  <p className="text-[#777] text-xs leading-relaxed">{r.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPLIANCE EXPERTISE ── */}
      <section className="bg-[#0d0d0d] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative rounded-2xl overflow-hidden order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=healthcare%20compliance%20expert%20reviewing%20ACHC%20accreditation%20standards%20documents%20at%20dark%20elegant%20desk%2C%20professional%20consultant%20with%20regulatory%20binders%20and%20compliance%20checklists%2C%20warm%20golden%20office%20lighting%2C%20sophisticated%20dark%20workspace%20with%20gold%20accents%2C%20focused%20professional%20reviewing%20survey%20readiness%20materials&width=700&height=500&seq=compliance001&orientation=landscape"
                alt="Dr. Villarreal ACHC compliance expertise"
                className="w-full h-80 object-cover object-top rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/60 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-5 left-5">
                <span className="bg-[#d4af37] text-[#0d0d0d] text-xs font-black px-4 py-2 rounded-full uppercase tracking-wider">
                  Survey-Ready Expertise
                </span>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-[#d4af37]"></div>
                <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Compliance &amp; Accreditation</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-5 leading-tight">
                Specialized Expertise in
                <br />
                <span className="text-[#d4af37]">Compliance &amp; Accreditation</span>
              </h2>
              <p className="text-[#999] text-base leading-relaxed mb-6">
                Dr. Villarreal understands that compliance is not optional — it is critical. His research highlights that agencies fail when they lack proper documentation systems, workflow integration, and regulatory readiness.
              </p>
              <div className="flex flex-col gap-4">
                {[
                  { icon: 'ri-calendar-check-line', text: 'Survey-ready at all times' },
                  { icon: 'ri-award-line', text: 'Structured for ACHC accreditation success' },
                  { icon: 'ri-settings-3-line', text: 'Built with compliance-driven systems from day one' },
                  { icon: 'ri-file-shield-2-line', text: 'Proper documentation systems and workflow integration' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#d4af37]/30 bg-[#d4af37]/5 shrink-0">
                      <i className={`${item.icon} text-[#d4af37] text-base`}></i>
                    </div>
                    <span className="text-[#ccc] text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OUTCOMES ── */}
      <section className="bg-[#111111] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#d4af37]"></div>
              <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Results-Driven</span>
              <div className="w-8 h-px bg-[#d4af37]"></div>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3">What You Will Gain</h2>
            <p className="text-[#888] text-base max-w-xl mx-auto">Dr. Villarreal&apos;s consulting is focused on real outcomes, not theory. His doctoral findings emphasize that agencies aligning technology, training, and leadership achieve higher efficiency, better compliance, and long-term sustainability.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
            {[
              'A fully structured operational system',
              'Clear policies and procedures',
              'Efficient workflow design',
              'Technology integration strategies',
              'Step-by-step licensing & accreditation guidance',
              'Compliance-driven systems from day one',
            ].map((o, i) => (
              <div key={i} className="flex items-center gap-4 bg-[#161616] border border-[#d4af37]/10 rounded-xl px-6 py-5 hover:border-[#d4af37]/30 transition-all duration-300">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#d4af37]/15 shrink-0">
                  <i className="ri-check-line text-[#d4af37] text-sm"></i>
                </div>
                <span className="text-[#ccc] text-sm font-medium">{o}</span>
              </div>
            ))}
          </div>

          <div className="bg-[#161616] border border-[#d4af37]/20 rounded-2xl p-8 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#d4af37]/15 mx-auto mb-4">
                  <i className="ri-book-open-line text-[#d4af37] text-3xl"></i>
                </div>
                <p className="text-white font-bold text-base mb-1">Academic Research</p>
                <p className="text-[#777] text-sm">Doctoral-level evidence-based strategies</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-[#d4af37] mx-auto mb-4">
                  <i className="ri-arrow-left-right-line text-[#d4af37] text-2xl"></i>
                </div>
                <p className="text-[#d4af37] font-black text-lg">Bridging the Gap</p>
                <p className="text-[#777] text-sm">Theory meets real-world execution</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[#d4af37]/15 mx-auto mb-4">
                  <i className="ri-rocket-line text-[#d4af37] text-3xl"></i>
                </div>
                <p className="text-white font-bold text-base mb-1">Scalable Growth</p>
                <p className="text-[#777] text-sm">Real-world operations &amp; business success</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST SIGNALS ── */}
      <section className="bg-[#0d0d0d] py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#d4af37]"></div>
              <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Why Clients Trust Dr. Villarreal</span>
              <div className="w-8 h-px bg-[#d4af37]"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-graduation-cap-line', text: 'Doctor of Business Administration (DBA)' },
              { icon: 'ri-building-2-line', text: 'Healthcare entrepreneur with multiple successful agencies' },
              { icon: 'ri-hospital-line', text: 'Proven experience in home health, home care & medical practices' },
              { icon: 'ri-flask-line', text: 'Evidence-based strategies backed by doctoral research' },
              { icon: 'ri-shield-check-line', text: 'Deep understanding of compliance, accreditation & operations' },
              { icon: 'ri-trophy-line', text: 'Committed to your long-term agency success' },
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-4 bg-[#161616] border border-[#d4af37]/10 rounded-xl px-5 py-5 hover:border-[#d4af37]/30 transition-all duration-300">
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#d4af37]/10 shrink-0">
                  <i className={`${t.icon} text-[#d4af37] text-base`}></i>
                </div>
                <span className="text-[#ccc] text-sm leading-relaxed">{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=dark%20luxury%20professional%20background%20with%20subtle%20gold%20geometric%20patterns%2C%20premium%20dark%20texture%20with%20warm%20golden%20glow%2C%20sophisticated%20corporate%20atmosphere%2C%20deep%20charcoal%20with%20metallic%20gold%20accents%2C%20high-end%20professional%20backdrop%20for%20healthcare%20consulting&width=1920&height=700&seq=cta_drv001&orientation=landscape"
            alt="Start building your agency"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/82"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-px bg-[#d4af37]"></div>
            <span className="text-[#d4af37] text-xs uppercase tracking-[3px] font-semibold">Start Today</span>
            <div className="w-8 h-px bg-[#d4af37]"></div>
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-5 leading-tight">
            Start Building Your Agency
            <br />
            <span className="text-[#d4af37]">the Right Way</span>
          </h2>
          <p className="text-[#b0b0b0] text-lg mb-10 leading-relaxed">
            Don&apos;t risk delays, compliance issues, or costly mistakes. Partner with Dr. Villarreal and get the expertise, structure, and support needed to achieve measurable results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services"
              className="whitespace-nowrap bg-[#d4af37] text-[#0d0d0d] font-bold text-sm px-10 py-4 rounded-full hover:bg-[#f4c430] transition-all duration-300 cursor-pointer"
            >
              Book a Consultation
            </Link>
            <Link
              to="/about"
              className="whitespace-nowrap border-2 border-white/30 text-white font-bold text-sm px-10 py-4 rounded-full hover:border-[#d4af37] hover:text-[#d4af37] transition-all duration-300 cursor-pointer"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
